import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import ConfirmDialog from '@/components/ConfirmDialog';
import api from '@/lib/api';
import { readPageCache, writePageCache } from '@/lib/pageCache';
import useRefreshOnFocus from '@/lib/useRefreshOnFocus';
import '@/pages/Products.css';
import './Clients.css';

const emptyClient = {
  client_type: 'person',
  name: '',
  fixed_id: '',
  optional_id: '',
  first_name: '',
  last_name: '',
  business_name: '',
  document: '',
  contact_name: '',
  email: '',
  phone: '',
  website: '',
  address: '',
  city: '',
  notes: '',
};

const buildClientName = (form) => {
  if (form.client_type === 'company') {
    return (form.business_name || '').trim();
  }
  return `${form.first_name || ''} ${form.last_name || ''}`.trim();
};

const normalizeClientForm = (client = {}) => {
  const type = client.client_type || 'person';
  const legacyName = client.name || '';
  const firstName = client.first_name || (type === 'person' ? legacyName : '');
  return {
    ...emptyClient,
    client_type: type,
    name: legacyName,
    fixed_id: client.fixed_id || '',
    optional_id: client.optional_id || '',
    first_name: firstName,
    last_name: client.last_name || '',
    business_name: client.business_name || (type === 'company' ? legacyName : ''),
    document: client.document || '',
    contact_name: client.contact_name || '',
    email: client.email || '',
    phone: client.phone || '',
    website: client.website || '',
    address: client.address || '',
    city: client.city || '',
    notes: client.notes || '',
  };
};

const ClientsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const cachedClients = readPageCache('clients-list');
  const [clients, setClients] = useState(cachedClients || []);
  const [loading, setLoading] = useState(!cachedClients);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyClient);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteClient, setDeleteClient] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadClients = useCallback(async (q = '') => {
    try {
      const { data } = await api.get('/clients', { params: q ? { q } : {} });
      setClients(data);
      if (!q) writePageCache('clients-list', data);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    const t = setTimeout(() => loadClients(search), 300);
    return () => clearTimeout(t);
  }, [loadClients, search]);

  useRefreshOnFocus(() => loadClients(search), { enabled: !modalOpen });

  const openNew = () => {
    setEditingId(null);
    setForm(emptyClient);
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (client) => {
    setEditingId(client.id);
    setForm(normalizeClientForm(client));
    setFormError('');
    setModalOpen(true);
  };

  const handleSave = async () => {
    setFormError('');
    const displayName = buildClientName(form);
    if (!displayName) {
      setFormError(form.client_type === 'company' ? 'La razon social es obligatoria' : 'El nombre es obligatorio');
      return;
    }
    const payload = {
      ...form,
      name: displayName,
    };
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/clients/${editingId}`, payload);
      } else {
        await api.post('/clients', payload);
      }
      setModalOpen(false);
      loadClients(search);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setFormError(typeof detail === 'string' ? detail : 'Error al guardar el cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteClient) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/clients/${deleteClient.id}`);
      setDeleteClient(null);
      loadClients(search);
    } catch (err) {
      alert('Error al eliminar cliente');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar activePath={location.pathname} />

      <main className="main-content">
        <div className="page-header-row">
          <div>
            <h1 data-testid="clients-title">Clientes</h1>
            <p className="dashboard-subtitle">
              Gestión de clientes y su historial de presupuestos
            </p>
          </div>
          <button
            className="btn-primary-inline"
            onClick={openNew}
            data-testid="new-client-button"
          >
            + Nuevo Cliente
          </button>
        </div>

        <div className="search-bar">
          <input
            type="text"
            className="form-input"
            placeholder="Buscar por nombre, ID, documento, email o telefono..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="clients-search-input"
          />
        </div>

        {loading ? (
          <div className="empty-state">Cargando clientes...</div>
        ) : clients.length === 0 ? (
          <div className="card">
            <div className="empty-state" data-testid="empty-clients">
              <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
                {search ? 'Sin resultados' : 'No hay clientes registrados'}
              </p>
              {!search && (
                <p style={{ fontSize: '0.875rem', color: '#A0A0A0' }}>
                  Comenzá agregando tu primer cliente
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID fijo</th>
                  <th>Nombre</th>
                  <th>Documento</th>
                  <th>Email</th>
                  <th>Teléfono</th>
                  <th>Dirección</th>
                  <th style={{ width: '220px', textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.id} data-testid={`client-row-${c.id}`}>
                    <td className="mono">{c.fixed_id || '-'}</td>
                    <td className="cell-strong">{c.name}</td>
                    <td>{c.document || '-'}</td>
                    <td>{c.email || '-'}</td>
                    <td>{c.phone || '-'}</td>
                    <td>{c.address || '-'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn-link"
                        onClick={() => navigate(`/presupuestos/nuevo?client=${c.id}`)}
                        data-testid={`new-budget-for-client-${c.id}`}
                      >
                        + Presupuesto
                      </button>
                      <button
                        className="btn-link"
                        onClick={() => openEdit(c)}
                        data-testid={`edit-client-${c.id}`}
                      >
                        Editar
                      </button>
                      <button
                        className="btn-link btn-link-danger"
                        onClick={() => setDeleteClient(c)}
                        data-testid={`delete-client-${c.id}`}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ===== MODAL ===== */}
        {modalOpen && (
          <div className="modal-backdrop" onClick={() => setModalOpen(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="client-modal">
              <div className="modal-header">
                <h3>{editingId ? 'Editar Cliente' : 'Nuevo Cliente'}</h3>
                <button className="btn-remove" onClick={() => setModalOpen(false)}>×</button>
              </div>

              <div className="modal-body">
                {formError && <div className="login-error" data-testid="client-form-error">{formError}</div>}

                <section className="client-form-section">
                  <h4 className="client-form-section-title">Informacion principal</h4>
                  <div className="client-form-grid">
                    <div className="form-group">
                      <label className="form-label">Tipo de cliente *</label>
                      <select
                        className="form-input"
                        value={form.client_type}
                        onChange={(e) => setForm({ ...form, client_type: e.target.value })}
                        data-testid="client-type-input"
                        autoFocus
                      >
                        <option value="person">Persona</option>
                        <option value="company">Empresa</option>
                      </select>
                    </div>

                    {form.client_type === 'company' ? (
                      <div className="form-group client-form-span-2">
                        <label className="form-label">Razon Social *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={form.business_name}
                          onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                          data-testid="client-business-name-input"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="form-group">
                          <label className="form-label">Nombre *</label>
                          <input
                            type="text"
                            className="form-input"
                            value={form.first_name}
                            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                            data-testid="client-first-name-input"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Apellido</label>
                          <input
                            type="text"
                            className="form-input"
                            value={form.last_name}
                            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                            data-testid="client-last-name-input"
                          />
                        </div>
                      </>
                    )}

                    <div className="form-group">
                      <label className="form-label">{form.client_type === 'company' ? 'CUIT' : 'DNI'}</label>
                      <input
                        type="text"
                        className="form-input"
                        value={form.document}
                        onChange={(e) => setForm({ ...form, document: e.target.value })}
                        data-testid="client-document-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">ID fijo</label>
                      <input
                        type="text"
                        className="form-input"
                        value={form.fixed_id}
                        onChange={(e) => setForm({ ...form, fixed_id: e.target.value })}
                        data-testid="client-fixed-id-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">ID opcional</label>
                      <input
                        type="text"
                        className="form-input"
                        value={form.optional_id}
                        onChange={(e) => setForm({ ...form, optional_id: e.target.value })}
                        data-testid="client-optional-id-input"
                      />
                    </div>
                  </div>
                </section>

                <section className="client-form-section">
                  <h4 className="client-form-section-title">Contacto</h4>
                  <div className="client-form-grid">
                    {form.client_type === 'company' && (
                      <div className="form-group client-form-span-2">
                        <label className="form-label">Nombre del contacto</label>
                        <input
                          type="text"
                          className="form-input"
                          value={form.contact_name}
                          onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                          placeholder="Andrea"
                          data-testid="client-contact-name-input"
                        />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">Telefono</label>
                      <input
                        type="text"
                        className="form-input"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        data-testid="client-phone-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-input"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="client-email-input"
                      />
                    </div>
                    <div className="form-group client-form-span-2">
                      <label className="form-label">Pagina web</label>
                      <input
                        type="text"
                        className="form-input"
                        value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                        data-testid="client-website-input"
                      />
                    </div>
                  </div>
                </section>

                <section className="client-form-section">
                  <h4 className="client-form-section-title">Direccion</h4>
                  <div className="client-form-grid">
                    <div className="form-group">
                      <label className="form-label">Direccion</label>
                      <input
                        type="text"
                        className="form-input"
                        value={form.address}
                        onChange={(e) => setForm({ ...form, address: e.target.value })}
                        data-testid="client-address-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Ciudad</label>
                      <input
                        type="text"
                        className="form-input"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        data-testid="client-city-input"
                      />
                    </div>
                  </div>
                </section>

                <section className="client-form-section">
                  <h4 className="client-form-section-title">Observaciones</h4>
                  <div className="form-group">
                    <label className="form-label">Notas</label>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      data-testid="client-notes-input"
                    />
                  </div>
                </section>

                {false && (<>
                <div className="form-group">
                  <label className="form-label">Nombre *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="client-name-input"
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="client-email-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="client-phone-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Dirección</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    data-testid="client-address-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Notas</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="client-notes-input"
                  />
                </div>
                </>)}
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
                <button
                  className="btn-primary-inline"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-client-button"
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        )}
        <ConfirmDialog
          open={!!deleteClient}
          title="Eliminar cliente"
          message={deleteClient ? `¿Estas seguro de eliminar el cliente "${deleteClient.name}"? Los presupuestos historicos se mantendran.` : ''}
          confirmLabel="Eliminar"
          loading={deleteLoading}
          onConfirm={handleDelete}
          onCancel={() => setDeleteClient(null)}
        />
      </main>
    </div>
  );
};

export default ClientsList;
